import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "../lib/api";
import { useAuth } from "./AuthContext";

const CartContext = createContext(null);

export function CartProvider({ children }) {
  const { user } = useAuth();
  const [cart, setCart] = useState({ items: [], total: 0, count: 0 });
  const [open, setOpen] = useState(false);
  const [customizeProductId, setCustomizeProductId] = useState(null);

  const refresh = useCallback(async () => {
    if (!user) {
      setCart({ items: [], total: 0, count: 0 });
      return;
    }
    try {
      const { data } = await api.get("/cart");
      setCart(data);
    } catch {
      /* ignore */
    }
  }, [user]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const addItem = async (product_id, quantity = 1) => {
    try {
      const { data } = await api.post("/cart/add", { product_id, quantity });
      setCart(data);
      setOpen(true);
      return { ok: true, cart: data };
    } catch (e) {
      // Backend returns 409 when the product needs customization — open the
      // ItemCustomizeSheet instead of silently failing.
      if (e?.response?.status === 409) {
        setCustomizeProductId(product_id);
        return { ok: false, needs_customization: true, product_id };
      }
      throw e;
    }
  };

  const openCustomize = (product_id) => setCustomizeProductId(product_id);
  const closeCustomize = () => setCustomizeProductId(null);

  const addCustomizedItem = async (product_id, selection, quantity = 1) => {
    const { data } = await api.post("/cart/add-item", {
      product_id,
      quantity,
      selection,
    });
    setCart(data);
    setOpen(true);
    return data;
  };

  // Legacy item helpers (keyed by product_id) — kept for backwards compat.
  const updateItem = async (product_id, quantity) => {
    const { data } = await api.post("/cart/update", { product_id, quantity });
    setCart(data);
    return data;
  };

  const removeItem = async (product_id) => {
    const { data } = await api.delete(`/cart/${product_id}`);
    setCart(data);
    return data;
  };

  // New line-based helpers (Phase 3)
  const updateLine = async (line_id, quantity) => {
    const { data } = await api.post("/cart/line/update", { line_id, quantity });
    setCart(data);
    return data;
  };

  const removeLine = async (line_id) => {
    const { data } = await api.delete(`/cart/line/${line_id}`);
    setCart(data);
    return data;
  };

  const setLocation = async (shop_id, location_id, confirm_clear = false) => {
    const { data } = await api.post("/cart/location", { shop_id, location_id, confirm_clear });
    setCart(data);
    return data;
  };

  return (
    <CartContext.Provider
      value={{
        cart,
        open,
        setOpen,
        addItem,
        addCustomizedItem,
        updateItem,
        removeItem,
        updateLine,
        removeLine,
        setLocation,
        refresh,
        customizeProductId,
        openCustomize,
        closeCustomize,
      }}
    >
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used inside CartProvider");
  return ctx;
}
